import iccNotificationService from '@/services/icc/iccNotificationService';
import log from '@/services/logger/log';
import {selectConsumerId} from '@/state/selectors/consumer';
import {
    selectPushNotificationIsDeletingFromBackend,
    selectPushNotificationIsStoringToBackend,
    selectPushNotificationIsUnsubscribing,
} from '@/state/selectors/pushNotification';
import {
    setPushNotificationDeletingFromBackend,
    setPushNotificationStoringToBackend,
    setPushNotificationSubscribing,
    setPushNotificationSubscription,
    setPushNotificationUnsubscribing,
} from '@/state/slices/pushNotificationSlice';
import {dispatch, getState} from '@/state/store';

import notificationStorageService from '../storage/notificationStorageService';
import pushNotificationProviders from './pushNotificationProviders';

let createSubscriptionPromise = null;

const getStoredSubscriptionInfo = () => {
    try {
        const subscriptionInfoRaw = notificationStorageService.getSubscriptionFromLocalStorage();

        return JSON.parse(subscriptionInfoRaw);
    } catch (e) {
        return undefined;
    }
};

const setSubscriptionInfo = (info) => {
    notificationStorageService.setSubscriptionToLocalStorage(JSON.stringify(info));
};

const clearSubscriptionInfo = () => {
    notificationStorageService.removeSubscriptionFromLocalStorage();
};

const createWebPushSubscription = async () => {
    if (createSubscriptionPromise) {
        return await createSubscriptionPromise;
    }

    createSubscriptionPromise = (async () => {
        dispatch(setPushNotificationSubscribing(true));
        try {
            const provider = pushNotificationProviders.getProvider();
            const subscription = await provider.subscribe();

            dispatch(setPushNotificationSubscription(subscription));

            return subscription;
        } finally {
            dispatch(setPushNotificationSubscribing(false));
            createSubscriptionPromise = null;
        }
    })();

    return await createSubscriptionPromise;
};

const getCurrentSubscription = async () => {
    const provider = pushNotificationProviders.getProvider();

    return await provider.getCurrentSubscription();
};

const storeSubscriptionToBackend = async (subscription) => {
    const isStoringToBackend = selectPushNotificationIsStoringToBackend(getState());

    if (isStoringToBackend) return;

    // If subscription is already stored, do not store it again
    const {data: dataFromStorage} = getStoredSubscriptionInfo() || {};

    const valueFromStorage = dataFromStorage?.endpoint || dataFromStorage?.token;
    const inputValue = subscription?.endpoint || subscription?.token;

    if (inputValue && inputValue === valueFromStorage) return;

    dispatch(setPushNotificationStoringToBackend(true));

    try {
        const subscriptionId = await iccNotificationService.pushSubscription(subscription);
        const consumerId = selectConsumerId(getState());

        const subscriptionData = {
            data: subscription,
            subscriptionId,
            consumerId,
        };

        setSubscriptionInfo(subscriptionData);
        log.debug('Subscription stored successfully');

        return true;
    } catch (e) {
        log.info(`Error storing subscription to backend: ${e}`);
        throw e;
    } finally {
        dispatch(setPushNotificationStoringToBackend(false));
    }
};

const deleteSubscriptionFromBackend = async (subscription) => {
    try {
        const {subscriptionId} = subscription || getStoredSubscriptionInfo() || {};

        if (!subscriptionId) return;

        const isDeletingFromBackend = selectPushNotificationIsDeletingFromBackend(getState());

        if (isDeletingFromBackend) return;

        dispatch(setPushNotificationDeletingFromBackend(true));
        await iccNotificationService.deleteSubscription(subscriptionId);

        // If subscription is provided, don't clear local storage
        // because there is new subscription data in local storage already
        if (!subscription) {
            clearSubscriptionInfo();
        }
    } catch (e) {
        log.info(`Error deleting subscription data: ${e}`);
    } finally {
        dispatch(setPushNotificationDeletingFromBackend(false));
    }
};

const unsubscribe = async (throwError = true) => {
    const subscription = await getCurrentSubscription();

    const isUnsubscribing = selectPushNotificationIsUnsubscribing(getState());

    if (isUnsubscribing) return;

    if (subscription) {
        dispatch(setPushNotificationUnsubscribing(true));
        try {
            const provider = pushNotificationProviders.getProvider();

            await provider.unsubscribe();
            dispatch(setPushNotificationSubscription(null));
            log.info('Unsubscribe successful');
        } catch (e) {
            log.info(`Unsubscribe error: ${e}`);
            if (throwError) throw e;
        } finally {
            dispatch(setPushNotificationUnsubscribing(false));
        }
    }
};

const unsubscribeAndDeleteSubscription = async (throwUnsubscribeError = true) => {
    await unsubscribe(throwUnsubscribeError);
    await deleteSubscriptionFromBackend();
};

export default {
    getStoredSubscriptionInfo,
    setSubscriptionInfo,
    clearSubscriptionInfo,
    createWebPushSubscription,
    getCurrentSubscription,
    storeSubscriptionToBackend,
    deleteSubscriptionFromBackend,
    unsubscribe,
    unsubscribeAndDeleteSubscription,
};
